import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Drawer,
  Form,
  Input,
  InputNumber,
  Switch,
  Row,
  Col,
  Typography,
  Popconfirm,
  message,
  Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '@/services/api';
import type { CmNationalTeam, CmPlayerCard } from '@/types';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface FormShape {
  abbreviation: string;
  teamName: string;
  jerseyId: number;
  rank: number;
  teamRating: number;
  teamStrengthBatting: number;
  teamStrengthBowling: number;
  teamStrengthFielding: number;
  playerCardsJson: string;
  isActive: boolean;
  displayOrder: number;
}

const CmNationalTeamsPage: React.FC = () => {
  const [teams, setTeams] = useState<CmNationalTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [form] = Form.useForm<FormShape>();

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCmNationalTeams({ limit: 100 });
      setTeams(res.data?.data ?? []);
    } catch (err) {
      console.error(err);
      message.error('Failed to load national teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTeams();
  }, [fetchTeams]);

  const openCreate = () => {
    setEditingId(null);
    setJsonError(null);
    form.resetFields();
    form.setFieldsValue({
      abbreviation: '',
      teamName: '',
      jerseyId: 0,
      rank: 1,
      teamRating: 70,
      teamStrengthBatting: 15,
      teamStrengthBowling: 15,
      teamStrengthFielding: 15,
      playerCardsJson: '[]',
      isActive: true,
      displayOrder: 0,
    });
    setDrawerOpen(true);
  };

  const openEdit = (team: CmNationalTeam) => {
    setEditingId(team.id);
    setJsonError(null);
    form.setFieldsValue({
      abbreviation: team.abbreviation,
      teamName: team.teamName,
      jerseyId: team.jerseyId,
      rank: team.rank,
      teamRating: team.teamRating,
      teamStrengthBatting: team.teamStrength.batting,
      teamStrengthBowling: team.teamStrength.bowling,
      teamStrengthFielding: team.teamStrength.fielding,
      playerCardsJson: JSON.stringify(team.playerCards, null, 2),
      isActive: team.isActive,
      displayOrder: team.displayOrder,
    });
    setDrawerOpen(true);
  };

  const formatJson = () => {
    const raw = form.getFieldValue('playerCardsJson');
    if (typeof raw !== 'string') return;
    try {
      const parsed = JSON.parse(raw);
      form.setFieldValue('playerCardsJson', JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON in playerCards');
    }
  };

  const handleSave = async () => {
    let values: FormShape;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    let playerCards: CmPlayerCard[];
    try {
      const parsed = JSON.parse(values.playerCardsJson) as unknown;
      if (!Array.isArray(parsed)) {
        throw new Error('playerCards must be a JSON array');
      }
      playerCards = parsed as CmPlayerCard[];
      setJsonError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid JSON';
      setJsonError(msg);
      message.error(`playerCards JSON is invalid: ${msg}`);
      return;
    }

    const payload = {
      abbreviation: values.abbreviation.toUpperCase().trim(),
      teamName: values.teamName,
      jerseyId: values.jerseyId,
      rank: values.rank,
      teamRating: values.teamRating,
      teamStrength: {
        batting: values.teamStrengthBatting,
        bowling: values.teamStrengthBowling,
        fielding: values.teamStrengthFielding,
      },
      playerCards,
      isActive: values.isActive,
      displayOrder: values.displayOrder,
    };

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.updateCmNationalTeam(editingId, payload);
        message.success(`${payload.abbreviation} updated`);
      } else {
        await adminApi.createCmNationalTeam(payload);
        message.success(`${payload.abbreviation} created`);
      }
      setDrawerOpen(false);
      await fetchTeams();
    } catch (err: unknown) {
      const apiErr = err as {
        response?: { data?: { message?: string | string[] } };
      };
      const apiMsg = Array.isArray(apiErr?.response?.data?.message)
        ? apiErr.response.data.message.join(', ')
        : apiErr?.response?.data?.message;
      message.error(apiMsg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (team: CmNationalTeam) => {
    try {
      await adminApi.deleteCmNationalTeam(team.id);
      message.success(`${team.abbreviation} deactivated`);
      await fetchTeams();
    } catch {
      message.error('Delete failed');
    }
  };

  const columns: ColumnsType<CmNationalTeam> = [
    {
      title: 'Abbr',
      dataIndex: 'abbreviation',
      key: 'abbreviation',
      width: 80,
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Team',
      dataIndex: 'teamName',
      key: 'teamName',
    },
    { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 70 },
    { title: 'Rating', dataIndex: 'teamRating', key: 'teamRating', width: 80 },
    { title: 'Jersey ID', dataIndex: 'jerseyId', key: 'jerseyId', width: 90 },
    {
      title: 'Players',
      key: 'players',
      width: 90,
      render: (_, r) => r.playerCards.length,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, r) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Popconfirm
            title="Soft-delete this team?"
            description="Sets isActive=false. The row is retained for audit recovery."
            onConfirm={() => handleDelete(r)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space
        direction="horizontal"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Custom Match — National Teams
          </Title>
          <Text type="secondary">
            DB-backed roster of teams served by <code>/config/custom-match/team-jsons</code>.
            Changes propagate to all backend instances within ~1s via pub/sub. After edits, bump{' '}
            <code>customMatchJsonVersion</code> in <strong>Config → Custom Match</strong> so clients
            drop their cached copy.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchTeams}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create Team
          </Button>
        </Space>
      </Space>

      <Card>
        <Table<CmNationalTeam>
          rowKey="id"
          loading={loading}
          dataSource={teams}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false }}
        />
      </Card>

      <Drawer
        title={editingId ? 'Edit National Team' : 'Create National Team'}
        width={720}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
      >
        <Form<FormShape> form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Abbreviation"
                name="abbreviation"
                rules={[
                  { required: true, message: 'Required' },
                  {
                    pattern: /^[A-Z]{2,8}$/,
                    message: '2-8 uppercase letters',
                  },
                ]}
              >
                <Input placeholder="IND" maxLength={8} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label="Team Name"
                name="teamName"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="India" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="Jersey ID"
                name="jerseyId"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Rank" name="rank" rules={[{ required: true, message: 'Required' }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Team Rating"
                name="teamRating"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Display Order"
                name="displayOrder"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Card size="small" title="Team Strength" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Batting" name="teamStrengthBatting" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Bowling" name="teamStrengthBowling" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Fielding"
                  name="teamStrengthFielding"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>
                  Player Cards (JSON array of <Text code>CmPlayerCard</Text> objects)
                </span>
                <Button size="small" onClick={formatJson}>
                  Format JSON
                </Button>
              </Space>
            }
            name="playerCardsJson"
            rules={[{ required: true, message: 'Required' }]}
          >
            <TextArea
              rows={22}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              placeholder='[ { "playerIndex": 0, "firstName": "Rohit", ... } ]'
            />
          </Form.Item>

          {jsonError && (
            <Alert
              type="error"
              showIcon
              message="Invalid JSON"
              description={jsonError}
              style={{ marginBottom: 16 }}
            />
          )}

          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            Saving updates the team data and triggers a cross-instance cache reload, but does{' '}
            <strong>not</strong> bump <code>customMatchJsonVersion</code>. After your edits, go to{' '}
            <strong>Config → Custom Match</strong> tab and bump the version so clients invalidate
            their local copy and re-fetch.
          </Paragraph>
        </Form>
      </Drawer>
    </div>
  );
};

export default CmNationalTeamsPage;
